import React, { useEffect, useRef, useState } from "react";
import ConfirmModal from "../ConfirmModal.js";
import SopChecklistForm, { blankAnswers, checklistState, toRunPayload } from "./SopChecklistForm.jsx";
import { registerSopHost } from "./sopGate.js";

/**
 * Mounted once (App.js). Opens when a gated request comes back SOP_REQUIRED
 * (see sopGate.js) and resolves the pending promise with the filled run.
 */
export default function SopChecklistHost() {
    const [state, setState] = useState(null); // { template, message }
    const [answers, setAnswers] = useState({});
    const [remarks, setRemarks] = useState("");
    const resolver = useRef(null);
    const kept = useRef({}); // answers survive a re-open for the same template

    useEffect(
        () =>
            registerSopHost((template, { message } = {}) =>
                new Promise((resolve) => {
                    resolver.current = resolve;
                    const prev = kept.current[template.templateid];
                    setAnswers(prev || blankAnswers(template));
                    setRemarks("");
                    setState({ template, message });
                })),
        [],
    );

    const close = (value) => {
        if (state && value) kept.current[state.template.templateid] = answers;
        if (!value) kept.current = {};
        setState(null);
        const r = resolver.current;
        resolver.current = null;
        r?.(value);
    };

    if (!state) return null;
    const { template, message } = state;
    const { complete, pass, failed } = checklistState(template, answers);

    return (
        <ConfirmModal
            isOpen
            wide
            icon="approve"
            confirmVariant="primary"
            title={`${template.name} · v${template.version}`}
            message={template.description || "Complete every step before continuing."}
            confirmText="Signed off — continue"
            confirmDisabled={!complete || !pass}
            onConfirm={() => close(toRunPayload(template, answers, { remarks: remarks || null }))}
            onCancel={() => close(null)}
        >
            {message && <div className="alert alert-warning py-2 small text-start">{message}</div>}
            <SopChecklistForm template={template} answers={answers} onChange={setAnswers} />
            <textarea
                className="form-control form-control-sm mt-3"
                rows={2}
                placeholder="Remarks (optional)"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
            />
            {complete && !pass && (
                <div className="small text-danger mt-2 text-start">
                    Not passed: {failed.map((f) => f.prompt).join("; ")}. The step can't go ahead until these are right.
                </div>
            )}
        </ConfirmModal>
    );
}
